import { useEffect, useRef, useState } from "react";
import { BarChart3, CalendarDays, History, LogOut, MoonStar, Settings2, Wine } from "lucide-react";
import type { AppConfig, BootstrapData } from "../shared/domain";
import { api, isDemoMode } from "./api/client";
import { DailyPage } from "./pages/DailyPage";
import { HistoryPage } from "./pages/HistoryPage";
import { MonthlyPage } from "./pages/MonthlyPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LoginPage } from "./pages/LoginPage";
import { NoGuestPage } from "./pages/NoGuestPage";
import { todayInJapan } from "./domain/form";
import { LoadingBlock } from "./components/ui";

type Route = "daily" | "no-guest" | "history" | "monthly" | "settings";

const routeFromPath = (): Route => {
  if (location.pathname.startsWith("/history")) return "history";
  if (location.pathname.startsWith("/monthly")) return "monthly";
  if (location.pathname.startsWith("/settings")) return "settings";
  if (location.pathname.startsWith("/no-guest")) return "no-guest";
  return "daily";
};

const paths: Record<Route, string> = {
  daily: "/",
  "no-guest": "/no-guest",
  history: "/history",
  monthly: "/monthly",
  settings: "/settings",
};

const navigation = [
  { route: "daily" as const, label: "日次入力", icon: CalendarDays },
  { route: "no-guest" as const, label: "ノーゲス", icon: MoonStar },
  { route: "history" as const, label: "履歴", icon: History },
  { route: "monthly" as const, label: "月次", icon: BarChart3 },
  { route: "settings" as const, label: "設定", icon: Settings2 },
];

export function App() {
  const [route, setRoute] = useState<Route>(routeFromPath);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [authError, setAuthError] = useState("");
  const initialLoadSequence = useRef(0);
  const [initialData, setInitialData] = useState<BootstrapData | null>(null);
  const [dailyDirty, setDailyDirty] = useState(false);

  const loadInitialData = () => {
    const sequence = ++initialLoadSequence.current;
    const fail = (error: Error) => {
      if (sequence === initialLoadSequence.current) setConfigError(error.message);
    };
    if (routeFromPath() === "daily") {
      const date = new URLSearchParams(location.search).get("date") ?? todayInJapan();
      api.getBootstrap(date).then((data) => {
        if (sequence !== initialLoadSequence.current) return;
        setInitialData(data);
        setConfig(data.config);
      }).catch(fail);
    } else {
      api.getConfig().then((data) => {
        if (sequence === initialLoadSequence.current) setConfig(data);
      }).catch(fail);
    }
  };

  useEffect(() => {
    let active = true;
    api.getSession()
      .then((session) => {
        if (!active) return;
        setAuthenticated(session.authenticated);
        if (session.authenticated) loadInitialData();
      })
      .catch((error: Error) => {
        if (!active) return;
        setAuthError(error.message);
        setAuthenticated(false);
      });
    const onPopState = () => {
      setInitialData(null);
      setRoute(routeFromPath());
    };
    const onUnauthorized = () => {
      initialLoadSequence.current += 1;
      setInitialData(null);
      setConfig(null);
      setAuthenticated(false);
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("dnc:unauthorized", onUnauthorized);
    return () => {
      active = false;
      initialLoadSequence.current += 1;
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("dnc:unauthorized", onUnauthorized);
    };
  }, []);

  const finishLogin = () => {
    setAuthenticated(true);
    setAuthError("");
    setConfigError("");
    loadInitialData();
  };

  const logout = async () => {
    if (dailyDirty && !window.confirm("未保存の変更があります。ログアウトしますか？")) return;
    initialLoadSequence.current += 1;
    await api.logout().catch(() => undefined);
    setInitialData(null);
    setConfig(null);
    setDailyDirty(false);
    setAuthenticated(false);
  };

  const navigate = (nextRoute: Route, search = "") => {
    if (dailyDirty && (route === "daily" || route === "no-guest") && !window.confirm("未保存の変更があります。画面を移動しますか？")) return;
    setInitialData(null);
    history.pushState({}, "", `${paths[nextRoute]}${search}`);
    setRoute(nextRoute);
    setDailyDirty(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (authenticated === null) return <LoadingBlock label="ログイン状態を確認しています" />;
  if (!authenticated) return <LoginPage onSuccess={finishLogin} configurationError={authError} />;

  return (
    <div className="app-shell">
      <header className="app-header">
        <button type="button" className="brand" onClick={() => navigate("daily")} aria-label="日次入力へ">
          <span className="brand__mark"><Wine size={20} aria-hidden="true" /></span>
          <span>
            <strong>D&amp;C Sales</strong>
            <small>Bar operation ledger</small>
          </span>
        </button>
        <nav className="desktop-nav" aria-label="メインナビゲーション">
          {navigation.map(({ route: itemRoute, label, icon: Icon }) => (
            <button
              type="button"
              key={itemRoute}
              className={route === itemRoute ? "is-active" : ""}
              onClick={() => navigate(itemRoute)}
              aria-current={route === itemRoute ? "page" : undefined}
            >
              <Icon size={17} aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>
        <div className="header-status">
          <span className="status-dot" aria-hidden="true" />
          Sheets連携
        </div>
        <button type="button" className="header-logout" onClick={logout} title="ログアウト">
          <LogOut size={16} aria-hidden="true" /><span>ログアウト</span>
        </button>
      </header>

      {isDemoMode ? (
        <div className="demo-banner" role="status">
          <strong>デモモード</strong>
          この端末内だけに保存されます。本番ではGoogleスプレッドシートが正本です。
        </div>
      ) : null}

      <main className="app-main">
        {configError ? (
          <div className="fatal-panel" role="alert">
            <h1>設定を読み込めませんでした</h1>
            <p>{configError}</p>
            <button type="button" className="button button--primary" onClick={() => location.reload()}>
              再読み込み
            </button>
          </div>
        ) : !config ? (
          <LoadingBlock label="店舗設定を読み込んでいます" />
        ) : route === "daily" ? (
          <DailyPage config={config} initialData={initialData} onDirtyChange={setDailyDirty} />
        ) : route === "no-guest" ? (
          <NoGuestPage config={config} onDirtyChange={setDailyDirty} />
        ) : route === "history" ? (
          <HistoryPage onOpenDate={(date) => navigate("daily", `?date=${date}`)} />
        ) : route === "monthly" ? (
          <MonthlyPage />
        ) : (
          <SettingsPage config={config} />
        )}
      </main>

      <nav className="mobile-nav" aria-label="メインナビゲーション">
        {navigation.map(({ route: itemRoute, label, icon: Icon }) => (
          <button
            type="button"
            key={itemRoute}
            className={route === itemRoute ? "is-active" : ""}
            onClick={() => navigate(itemRoute)}
            aria-current={route === itemRoute ? "page" : undefined}
          >
            <Icon size={20} aria-hidden="true" />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
